import { ref, computed, shallowRef } from 'vue'
import type { AudioFile, TimeRange, WaveformData, ExportFormat, EditMode } from '@/types'

const SUPPORTED_FORMATS = [
  'audio/mpeg',
  'audio/mp3',
  'audio/wav',
  'audio/wave',
  'audio/ogg',
  'audio/aac',
  'audio/flac',
  'audio/x-flac',
  'audio/mp4',
  'audio/m4a',
  'audio/x-m4a'
]

export const MAX_FILE_SIZE = 100 * 1024 * 1024 // 100MB

const VALID_EXTENSIONS = ['.mp3', '.wav', '.ogg', '.aac', '.flac', '.m4a']

// Validate a user-selected file (size + MIME type / extension)
export const validateFile = (file: File): { valid: boolean; error?: string } => {
  if (!file) {
    return { valid: false, error: 'invalidFile' }
  }

  if (file.size > MAX_FILE_SIZE) {
    return { valid: false, error: 'fileTooLarge' }
  }

  const isValidMime = SUPPORTED_FORMATS.some(format => file.type.includes(format.split('/')[1]))
  const hasValidExtension = VALID_EXTENSIONS.some(ext => file.name.toLowerCase().endsWith(ext))

  if (!isValidMime && !hasValidExtension) {
    return { valid: false, error: 'unsupportedFormat' }
  }

  return { valid: true }
}

// Map a decode failure to an i18n error key. Running out of memory while
// decoding a big file surfaces as a RangeError / QuotaExceededError.
export const decodeErrorKey = (err: unknown): string => {
  const name = err instanceof Error ? err.name : ''
  return err instanceof RangeError || name === 'QuotaExceededError' ? 'fileTooLarge' : 'invalidFile'
}

export function useAudio() {
  const audioFile = ref<AudioFile | null>(null)
  const audioBuffer = shallowRef<AudioBuffer | null>(null)
  const waveformData = shallowRef<WaveformData | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  
  // Audio context (lazy initialization)
  let audioContext: AudioContext | null = null
  
  const getAudioContext = (): AudioContext => {
    if (!audioContext) {
      audioContext = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)()
    }
    return audioContext
  }
  
  // Load audio file
  const loadAudioFile = async (file: File): Promise<void> => {
    const validation = validateFile(file)
    if (!validation.valid) {
      error.value = validation.error || 'invalidFile'
      throw new Error(validation.error)
    }
    
    isLoading.value = true
    error.value = null
    
    try {
      const ctx = getAudioContext()
      // Resume inside the user gesture so autoplay after decoding isn't blocked (Safari)
      ctx.resume().catch(() => { /* ignore */ })
      
      // Read file as ArrayBuffer
      const arrayBuffer = await file.arrayBuffer()
      
      // Decode audio data
      const buffer = await ctx.decodeAudioData(arrayBuffer)
      
      audioBuffer.value = buffer
      
      audioFile.value = {
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        duration: buffer.duration,
        sampleRate: buffer.sampleRate,
        numberOfChannels: buffer.numberOfChannels,
        audioBuffer: buffer
      }
      
      // Generate waveform data
      waveformData.value = generateWaveformData(buffer)
      
    } catch (err) {
      error.value = decodeErrorKey(err)
      throw err
    } finally {
      isLoading.value = false
    }
  }
  
  /**
   * Find min and max values in a range with smoothing.
   * Uses stepping and averaging for a "crispier" look (matching old implementation).
   */
  const getMinMaxInRange = (
    array: Float32Array,
    start: number,
    end: number
  ): [number, number] => {
    let min = 0
    let min1 = 0
    let max = 0
    let max1 = 0
    const step = Math.max(1, Math.floor((end - start) / 15))

    for (let i = start; i < end; i += step) {
      const current = array[i]
      if (current < min) {
        min1 = min
        min = current
      } else if (current > max) {
        max1 = max
        max = current
      }
    }

    return [(min + min1) / 2, (max + max1) / 2]
  }

  // Generate waveform peaks for visualization (asymmetric min/max like old version)
  const generateWaveformData = (buffer: AudioBuffer, targetWidth = 2000): WaveformData => {
    const channelData = buffer.getChannelData(0) // Use first channel
    const dataLength = channelData.length
    const size = dataLength / targetWidth
    
    const minPeaks = new Float32Array(targetWidth)
    const maxPeaks = new Float32Array(targetWidth)
    
    let current = 0
    let globalMax = 0
    
    // First pass: calculate min/max for each segment
    for (let i = 0; i < targetWidth; i++) {
      const start = Math.floor(current)
      current += size
      const end = Math.floor(current)
      
      const [min, max] = getMinMaxInRange(channelData, start, end)
      minPeaks[i] = min
      maxPeaks[i] = max
      
      // Track global max for normalization
      if (Math.abs(min) > globalMax) globalMax = Math.abs(min)
      if (Math.abs(max) > globalMax) globalMax = Math.abs(max)
    }
    
    // Normalize: scale all peaks so the loudest part reaches full height
    // This makes quiet songs look similar to loud songs
    if (globalMax > 0) {
      const scale = 1 / globalMax
      for (let i = 0; i < targetWidth; i++) {
        minPeaks[i] *= scale
        maxPeaks[i] *= scale
      }
    }
    
    return {
      minPeaks,
      maxPeaks,
      length: targetWidth,
      sampleRate: buffer.sampleRate,
      duration: buffer.duration
    }
  }
  
  // Extract audio segment based on edit mode
  // Cut mode: keep the selected portion (start to end)
  // Trim mode: remove the selected portion (keep 0 to start + end to duration)
  const extractSegment = (range: TimeRange, mode: EditMode = 'cut'): Float32Array[] => {
    if (!audioBuffer.value) {
      throw new Error('No audio loaded')
    }
    
    const buffer = audioBuffer.value
    const startSample = Math.floor(range.start * buffer.sampleRate)
    const endSample = Math.floor(range.end * buffer.sampleRate)
    
    const channels: Float32Array[] = []
    
    if (mode === 'cut') {
      // Cut mode: extract only the selected portion
      const length = endSample - startSample
      
      for (let ch = 0; ch < buffer.numberOfChannels; ch++) {
        const channelData = buffer.getChannelData(ch)
        const segment = new Float32Array(length)
        
        for (let i = 0; i < length; i++) {
          segment[i] = channelData[startSample + i]
        }
        
        channels.push(segment)
      }
    } else {
      // Trim mode: remove the selected portion, keep beginning and end
      const totalSamples = buffer.length
      const beginningLength = startSample
      const endingLength = totalSamples - endSample
      const length = beginningLength + endingLength
      
      for (let ch = 0; ch < buffer.numberOfChannels; ch++) {
        const channelData = buffer.getChannelData(ch)
        const segment = new Float32Array(length)
        
        // Copy beginning portion (0 to start)
        for (let i = 0; i < beginningLength; i++) {
          segment[i] = channelData[i]
        }
        
        // Copy ending portion (end to duration)
        for (let i = 0; i < endingLength; i++) {
          segment[beginningLength + i] = channelData[endSample + i]
        }
        
        channels.push(segment)
      }
    }
    
    return channels
  }
  
  // Get duration of selection
  const selectionDuration = computed(() => {
    return audioFile.value?.duration || 0
  })
  
  // Reset state
  const reset = () => {
    audioFile.value = null
    audioBuffer.value = null
    waveformData.value = null
    error.value = null
    isLoading.value = false
  }
  
  return {
    audioFile,
    audioBuffer,
    waveformData,
    isLoading,
    error,
    selectionDuration,
    validateFile,
    loadAudioFile,
    extractSegment,
    generateWaveformData,
    getAudioContext,
    reset
  }
}

// Export helper for format detection
export function getExportMimeType(format: ExportFormat): string {
  const mimeTypes: Record<ExportFormat, string> = {
    mp3: 'audio/mpeg',
    wav: 'audio/wav'
  }
  return mimeTypes[format]
}

export function getExportExtension(format: ExportFormat): string {
  return `.${format}`
}
